"use client";

import {useSearchParams} from "next/navigation";
import {useMemo} from "react";
import {Document} from "~/app/components/document/document.model";
import {parseDocumentationFilter} from "~/app/components/document/document.filters";
import {calcPageCount, paginate} from "~/app/components/pagination/pagination";
import {PaginationBar} from "~/app/components/pagination/PaginationBar";
import DocumentEntry from "~/app/components/document/documentEntry";
import {Box} from "@mui/material";
import {DocumentationFilterBar} from "~/app/components/document/DocumentFilterBar";
import {PressPost} from "~/features/press/model";

type Props = {
  documents: Document[];
};

export function DocumentationClient({documents}: Props) {
  const searchParams = useSearchParams();

  const filter = useMemo(() => {
    return parseDocumentationFilter(
        new URLSearchParams(searchParams.toString())
    );
  }, [searchParams]);

  function sortByDateDesc(docs:Document[]) {
    return [...docs].sort(
      (a, b) =>
        new Date(b.date).getTime() -
        new Date(a.date).getTime()
    );
  }

  const page = Number(searchParams.get("page") ?? 1);

  const filtered = useMemo(() => {
    return documents.filter((doc) => {
      if (filter.origin && doc.origin.code !== filter.origin) return false;
      if (filter.authority && doc.authorityType !== filter.authority) return false;
      if (filter.content && !doc.contentTypes?.includes(filter.content)) return false;
      return true;
    });
  }, [documents, filter]);

  const sorted = sortByDateDesc(filtered);
  const pageCount = calcPageCount(sorted);
  const safePage = Math.min(page, pageCount || 1);
  const paginated = paginate(sorted, safePage);

  return (
      <>
        <DocumentationFilterBar documents={documents}/>

        <Box>
          {paginated.map((doc) => (
              <DocumentEntry key={doc.href} document={doc}/>
          ))}
        </Box>

        <PaginationBar
            route="/documentation"
            page={safePage}
            pageCount={pageCount}
            searchParams={Object.fromEntries(searchParams.entries())}
        />
      </>
  );
}
